import React, { useState, useEffect } from "react";
import { Award, Languages, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Team = () => {
  const [team, setTeam] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/team`);
        setTeam(response.data);
      } catch (error) {
        console.error("Error fetching team:", error);
      }
    };
    fetchTeam();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, team.length - 3));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.max(1, team.length - 3)) % Math.max(1, team.length - 3));
  };

  const visibleTeam = team.slice(currentIndex, currentIndex + 4);

  return (
    <section
      id="team"
      className="py-20 md:py-28 bg-[#F9FAFB]"
      data-testid="team-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div className="max-w-2xl">
            <span className="text-[#0F4C3A] font-semibold text-sm uppercase tracking-wider">
              Our Team
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Meet Your <span className="text-[#0F4C3A]">Care Experts</span>
            </h2>
            <p className="mt-6 text-lg text-gray-600">
              Our team of certified professionals brings decades of combined experience 
              in physiotherapy, massage therapy, kinesiology, and specialized treatments.
            </p>
          </div>

          {/* Navigation Arrows */}
          <div className="flex gap-3 mt-8 md:mt-0">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors"
              aria-label="Previous team members"
              data-testid="team-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors"
              aria-label="Next team members"
              data-testid="team-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Team Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visibleTeam.map((member, index) => (
            <div
              key={member.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 card-hover"
              data-testid={`team-card-${member.id}`}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Name overlay */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="font-bold text-lg">{member.name}</h3>
                  <p className="text-sm text-white/80">{member.title}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-gray-600 text-sm line-clamp-3">
                  {member.bio}
                </p>

                {/* Certifications */}
                <div className="flex items-start gap-2">
                  <Award className="w-4 h-4 text-[#0F4C3A] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {member.certifications.slice(0, 3).join(" • ")}
                  </p>
                </div>

                {/* Languages */}
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-[#0F4C3A] flex-shrink-0" />
                  <p className="text-xs text-gray-500">
                    {member.languages.join(", ")}
                  </p>
                </div>

                {/* View Profile */}
                <Link
                  to={`/team/${member.slug}`}
                  className="text-sm font-semibold text-[#0F4C3A] hover:text-[#1A6B52] inline-flex items-center gap-1 transition-colors"
                  data-testid={`team-view-profile-${member.id}`}
                >
                  View Full Profile
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="https://bc-ice.janeapp.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="team-book-btn"
          >
            <Button className="bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              Book with Our Team
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Team;
